import React from 'react';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';

import SectionCard from '../../../components/agentProfile/SectionCard';

import { initialsFrom, colorFor, subtitleFor } from '../utils';
import EmptyInline from '../EmptyInline';

/**
 * FindAgentsBar — sticky "Find agents" search input at the top of the
 * page + the cross-tenant search-results panel that drops below it once
 * the user types ≥2 characters.
 *
 * Hits /leadflow/network/search across the whole tenant ecosystem,
 * not just the current user's connections. Per-result CTA depends on
 * the connection state with that profile:
 *   • already connected   → "Connected" tag
 *   • request already sent → "Request sent" tag
 *   • neither              → "Connect" button
 *
 * Sticky positioning is measured by the parent — `top` is whatever
 * pixel offset clears the topbar on the current viewport. The wrapper
 * ref is forwarded so the parent can measure the rendered height for
 * the downstream sidebar's sticky offset.
 *
 * Props:
 *   wrapperRef         — ref forwarded onto the sticky wrapper div
 *   topPx              — number of pixels for the sticky `top` offset
 *   findSearch         — current search input value
 *   onFindSearchChange — input onChange handler (gets the new string)
 *   searching          — whether a debounced search is in flight
 *   searchedQuery      — the query that was actually searched (used
 *                        for "results for X" / no-match copy)
 *   searchResults      — array of cross-tenant agent profiles, each
 *                        carrying a `connection_status` field from the
 *                        backend ('connected' | 'pending_sent' |
 *                        'pending_received' | 'none')
 *   actionLoading      — id of the profile whose Connect button is busy
 *   onConnect          — (id, fullName) => void  (fires the Connect CTA)
 *   onView             — (id) => void            (fires when row is clicked)
 */
const FindAgentsBar = ({
    wrapperRef,
    topPx,
    findSearch,
    onFindSearchChange,
    searching,
    searchedQuery,
    searchResults,
    actionLoading,
    onConnect,
    onView,
}) => (
    <>
        {/* Sticky wrapper — `top` is dynamically measured by the parent
            so it clears whatever chrome is fixed at the top — works on
            mobile (taller topbar) and desktop (shorter topbar) without
            hardcoded breakpoints. Surface-ground bg + bottom padding so
            the strip below doesn't bleed through. */}
        <div
            ref={wrapperRef}
            style={{
                position: 'sticky',
                top: `${topPx}px`,
                zIndex: 5,
                backgroundColor: 'var(--surface-ground)',
                paddingBottom: '0.75rem',
                marginBottom: '0.25rem',
            }}
        >
            <Card
                className="shadow-1"
                pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
            >
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        value={findSearch}
                        onChange={(e) => onFindSearchChange(e.target.value)}
                        placeholder="Find agents by name, product, or city…"
                        className="w-full p-inputtext-sm"
                    />
                </IconField>
            </Card>
        </div>

        {/* Results panel — appears once the user types ≥2 chars. */}
        {findSearch.trim().length >= 2 && (
            <SectionCard
                icon="pi-search"
                title={
                    searching
                        ? 'Searching agents…'
                        : `Search results${searchedQuery ? ` for "${searchedQuery}"` : ''}`
                }
                subtitle={
                    !searching && searchedQuery
                        ? `${searchResults.length} ${searchResults.length === 1 ? 'agent' : 'agents'} found`
                        : undefined
                }
                className="mb-3"
            >
                {searching ? (
                    <div className="flex flex-column gap-2">
                        <Skeleton width="100%" height="3.5rem" />
                        <Skeleton width="100%" height="3.5rem" />
                        <Skeleton width="100%" height="3.5rem" />
                    </div>
                ) : searchResults.length === 0 ? (
                    <EmptyInline
                        icon="pi-search"
                        title="No agents found"
                        description={`Try a different name, product, or city than "${searchedQuery}".`}
                    />
                ) : (
                    <ul className="list-none p-0 m-0">
                        {searchResults.map((person, idx) => {
                            const id = person.id || person.profile_id;
                            const color = colorFor(id);
                            /* Backend-computed status drives the CTA. Falling
                               back to 'none' keeps old responses (without the
                               field) showing the Connect button. */
                            const status = person.connection_status || 'none';
                            return (
                                <li key={id || idx}>
                                    <div
                                        className="flex align-items-center gap-3 py-2 cursor-pointer hover:surface-50 border-round px-2"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => id && onView(id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                id && onView(id);
                                            }
                                        }}
                                    >
                                        <Avatar
                                            label={initialsFrom(person.full_name)}
                                            image={person.profile_photo_url_full || person.profile_photo_url || person.profile_photo || person.avatar || person.photo_url || null}
                                            shape="circle"
                                            style={{
                                                width: '2.75rem',
                                                height: '2.75rem',
                                                backgroundColor: `var(--${color}-100)`,
                                                color: `var(--${color}-700)`,
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                border: '2px solid var(--surface-0)',
                                                boxShadow: `0 0 0 2px var(--${color}-200)`,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {person.full_name || 'Unknown'}
                                            </div>
                                            {(() => {
                                                const subtitle = subtitleFor(person);
                                                return subtitle ? (
                                                    <div className="text-600 text-xs line-height-3 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                        {subtitle}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {person.location_city && (
                                                <div className="text-500 text-xs mt-1">
                                                    <i
                                                        className="pi pi-map-marker mr-1"
                                                        style={{ fontSize: '0.6rem' }}
                                                        aria-hidden="true"
                                                    />
                                                    {person.location_city}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            className="flex-shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {status === 'connected' ? (
                                                <Tag
                                                    value="Connected"
                                                    icon="pi pi-check"
                                                    severity="success"
                                                    style={{ fontSize: '0.65rem' }}
                                                />
                                            ) : status === 'pending_sent' ? (
                                                <Tag
                                                    value="Request sent"
                                                    icon="pi pi-clock"
                                                    severity="warning"
                                                    style={{ fontSize: '0.65rem' }}
                                                />
                                            ) : status === 'pending_received' ? (
                                                <Tag
                                                    value="Wants to connect"
                                                    icon="pi pi-inbox"
                                                    severity="info"
                                                    style={{ fontSize: '0.65rem' }}
                                                />
                                            ) : (
                                                <Button
                                                    label="Connect"
                                                    icon="pi pi-plus"
                                                    size="small"
                                                    rounded
                                                    outlined
                                                    loading={actionLoading === id}
                                                    disabled={actionLoading === id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onConnect(id, person.full_name);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {idx < searchResults.length - 1 && <Divider className="my-0" />}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </SectionCard>
        )}
    </>
);

export default React.memo(FindAgentsBar);
